/** @jsx jsx */
import React, { Children, useMemo } from 'react';
import { css, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/new';
import EmptyState from '@atlaskit/empty-state';
import { Stack, xcss, Box, Text } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';
import { SmartCardProvider } from '@atlaskit/link-provider';
import BreadcrumbRenderer from '@atlassian/jira-breadcrumbs/src/breadcrumb-renderer/index.tsx';
import ProjectBreadcrumb from '@atlassian/jira-breadcrumbs/src/common-breadcrumbs/project/index.tsx';
import PageHeader from '@atlassian/jira-common-components-page-header/src/index.tsx';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import {
	type JiraProjectType,
	JIRA_PROJECT_TYPE_CORE_PROJECT,
	JIRA_PROJECT_TYPE_SOFTWARE_PROJECT,
	TEAM_MANAGED_PROJECT,
	convertToProjectType,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { ViewExperienceTrackingProvider } from '@atlassian/jira-common-experience-tracking-viewing/src/view/experience-tracking-provider/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { ENTRYPOINT_ID_JIRA_SOFTWARE_PROJECT } from '@atlassian/jira-feedback-collector/src/constants.tsx';
import { FeedbackCollectorButton } from '@atlassian/jira-feedback-collector/src/ui/button/index.tsx';
import { DocumentTitle } from '@atlassian/jira-global-document-title/src/DocumentTitle.tsx';
import genericEmptyStateImage from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/empty/assets/generic-empty-state.svg';
import { useIntl, FormattedMessage } from '@atlassian/jira-intl';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { JIRA_SOFTWARE, JIRA_CORE } from '@atlassian/jira-shared-types/src/application.tsx';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/sub-product-updater/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { FEEDBACK_GROUP_ID } from '../common/constants.tsx';
import messages from './messages.tsx';
import { ProjectSummary } from './project-summary/index.tsx';

export const SummaryPage = ({
	projectData,
	useRouteResource,
	isEmbed = false,
}: {
	projectData: {
		key: string;
		projectId: string;
		name: string;
		projectStyle: string;
		// TODO clean this up as a part of HL-1433
		// eslint-disable-next-line @atlassian/relay/no-future-added-value
		projectType: JiraProjectType | '%future added value';
	};
	// TODO clean up or refactor as a part of HL-1930
	useRouteResource?: boolean;
	isEmbed?: boolean;
}) => {
	const { formatMessage } = useIntl();
	const appEditions = useAppEditions();

	const analyticsDataAttributes = useMemo(
		() => ({
			// convertToProjectType handles the case where projectData.projectType is invalid
			// @ts-expect-error TS2322: Type '%future added value' is not assignable to type JiraProjectType
			projectType: convertToProjectType(projectData.projectType),
		}),
		[projectData.projectType],
	);

	const isSimplifiedProject = projectData.projectStyle === TEAM_MANAGED_PROJECT;

	const experienceTrackerAttributes = useMemo(
		() => ({
			simplified: isSimplifiedProject,
		}),
		[isSimplifiedProject],
	);

	const documentTitle = `${formatMessage(messages.title)} - ${projectData.name}`;

	if (
		projectData.projectType !== JIRA_PROJECT_TYPE_CORE_PROJECT &&
		projectData.projectType !== JIRA_PROJECT_TYPE_SOFTWARE_PROJECT
	) {
		throw new Error(`Unsupported project type: ${projectData.projectType}`);
	}

	const { edition, application, subProduct } = {
		[JIRA_PROJECT_TYPE_CORE_PROJECT]: {
			edition: appEditions.core,
			application: JIRA_CORE,
			subProduct: 'core' as const,
		},
		[JIRA_PROJECT_TYPE_SOFTWARE_PROJECT]: {
			edition: appEditions.software,
			application: JIRA_SOFTWARE,
			subProduct: 'software' as const,
		},
	}[projectData.projectType];

	const breadcrumb = (
		<ProjectBreadcrumb
			projectKey={projectData.key}
			projectName={projectData.name}
			projectId={projectData.projectId}
			isSimplifiedProject={isSimplifiedProject}
		>
			<BreadcrumbRenderer />
		</ProjectBreadcrumb>
	);

	// business (core) projects already have the "nav4 styling" applied originally without the FF turned on
	const shouldShowPageHeader =
		!getWillShowNav4() && projectData.projectType !== JIRA_PROJECT_TYPE_CORE_PROJECT && !isEmbed;

	return (
		<UFOSegment name="jira-summary">
			<DocumentTitle title={documentTitle} />
			<ContextualAnalyticsData
				objectType="summary"
				sourceType={SCREEN}
				sourceName="summary"
				attributes={analyticsDataAttributes}
			>
				<JSErrorBoundary
					teamName="Helios - Reports"
					fallback={() => (
						<EmptyState
							header={formatMessage(messages.emptyStateTitle)}
							description={formatMessage(messages.emptyStateDescription)}
							primaryAction={
								// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
								<Button appearance="primary" onClick={() => window.location.reload()}>
									{formatMessage(messages.emptyStateRefreshButton)}
								</Button>
							}
							imageUrl={genericEmptyStateImage}
						/>
					)}
					id="summaryPage"
					packageName="jiraSpaAppsSoftwareSummary"
				>
					<ViewExperienceTrackingProvider
						experience="viewSummary"
						experienceId={projectData.projectId}
						analyticsSource="summary"
						edition={edition}
						application={application}
						additionalAttributes={experienceTrackerAttributes}
					>
						<SmartCardProvider>
							<Stack xcss={containerStyles}>
								{shouldShowPageHeader ? (
									<Box xcss={headerContainerStyles}>
										<PageHeader truncateTitle breadcrumbs={breadcrumb}>
											{formatMessage(messages.title)}
										</PageHeader>
									</Box>
								) : (
									<Box xcss={contentTopPaddingStyles} />
								)}
								<ProjectSummary
									projectKey={projectData.key}
									projectId={projectData.projectId}
									projectType={projectData.projectType}
									useRouteResource={useRouteResource}
								/>
								<div css={promptContainerStyles}>
									<FormattedMessage
										{...messages.feedbackPrompt}
										values={{
											message: (chunks) => <Text>{chunks}</Text>,
											button: (chunks) => {
												const feedbackButtonCustomLabel = Children.toArray(chunks).join('');

												return (
													<FeedbackCollectorButton
														entrypointId={ENTRYPOINT_ID_JIRA_SOFTWARE_PROJECT}
														feedbackGroupId={FEEDBACK_GROUP_ID}
														feedbackButtonCustomLabel={feedbackButtonCustomLabel}
													/>
												);
											},
										}}
									/>
								</div>
							</Stack>
						</SmartCardProvider>
					</ViewExperienceTrackingProvider>
				</JSErrorBoundary>
				<SubProductUpdater subProduct={subProduct} />
				<FireScreenAnalytics />
			</ContextualAnalyticsData>
		</UFOSegment>
	);
};

const containerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '100%',
	paddingInline: 'space.300',
	paddingBlockEnd: 'space.300',
	marginInline: 'auto',
	maxWidth: '1200px',
});
const headerContainerStyles = xcss({ width: '100%' });
const contentTopPaddingStyles = xcss({ paddingBlockStart: 'space.300' });
const promptContainerStyles = css({
	alignItems: 'center',
	display: 'flex',

	marginTop: token('space.400', '32px'),
	paddingLeft: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	background: Tokens.ELEVATION_SURFACE_CONTRAST,
	borderRadius: token('border.radius.100', '4px'),

	gap: token('space.100', '8px'),
	[media.above.xxs]: {
		flexDirection: 'column',
	},
	[media.above.sm]: {
		flexDirection: 'row',
	},
	// we need to apply a CSS variable color to components that don't accept it so we need to override this way
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-important-styles
		color: `${Tokens.COLOR_TEXT} !important`,
	},
});
